import axios from 'axios';

// API基础配置
export const API_BASE_URL = 'https://bunnyrent-backend-917484933271.us-central1.run.app/api';

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理token过期
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // 刷新token失败，清除本地存储并跳转到登录页
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/auth';
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// 认证相关API
export const authAPI = {
  // 用户注册
  register: (userData) => api.post('/auth/register/', userData),
  
  // 用户登录
  login: (credentials) => api.post('/auth/login/', credentials),
  
  // 检查房东邮箱
  checkLandlordEmail: (email) => api.post('/auth/check-landlord-email/', { email }),
  
  // 用户登出
  logout: () => api.post('/auth/logout/'),
  
  // 获取用户资料
  getProfile: () => api.get('/auth/profile/'),
  
  // 更新用户资料
  updateProfile: (profileData) => {
    const config = {
      headers: {
        'Content-Type': profileData instanceof FormData ? 'multipart/form-data' : 'application/json',
      },
    };
    return api.put('/auth/profile/', profileData, config);
  },
  
  // 请求密码重置
  requestPasswordReset: (data) => api.post('/auth/password/reset/', data),
  
  // 确认密码重置
  confirmPasswordReset: (data) => api.post('/auth/password/reset/confirm/', data),
  
  // 修改密码
  changePassword: (data) => api.post('/auth/password-change/', data),
};

// 房源相关API
export const propertyAPI = {
  // 获取房源列表
  getProperties: (params) => api.get('/properties/', { params }),
  
  // 获取单个房源
  getProperty: (id) => api.get(`/properties/${id}/`),
  
  // 创建房源
  createProperty: (propertyData) => api.post('/properties/', propertyData),
  
  // 更新房源
  updateProperty: (id, propertyData) => api.put(`/properties/${id}/`, propertyData),
  
  // 删除房源
  deleteProperty: (id) => api.delete(`/properties/${id}/`),
  
  // 获取我的房源列表
  getMyListings: () => api.get('/properties/my-listings/'),
};

// 搜索相关API
export const searchAPI = {
  // 高级搜索
  search: (searchData) => api.post('/search/', searchData),
  
  // 获取保存的搜索
  getSavedSearches: () => api.get('/search/saved/'),
};

// 地图相关API
export const mapAPI = {
  // 获取地图房源数据
  getMapProperties: (params) => api.get('/maps/properties/', { params }),
  
  // 地址转坐标
  geocode: (address) => api.post('/maps/geocode/', { address }),
};

// 室友相关API
export const roommateAPI = {
  // 创建室友帖子
  createRoommatePost: (data) => api.post('/roommates/posts/', data),

  // 获取所有室友帖子
  getRoommatePosts: (params) => api.get('/roommates/posts/', { params }),

  // 获取单个室友帖子详情
  getRoommatePost: (slugOrId) => api.get(`/roommates/posts/${slugOrId}/`),

  // 获取当前用户的室友帖子
  getMyRoommatePosts: () => api.get('/roommates/my-posts/'),

  // 编辑室友帖子
  updateRoommatePost: (slugOrId, data) => api.put(`/roommates/posts/${slugOrId}/`, data),

  // 删除室友帖子
  deleteRoommatePost: (id) => api.delete(`/roommates/posts/${id}/`),

  // 切换帖子激活状态
  toggleRoommatePostStatus: (id) => api.post(`/roommates/posts/${id}/toggle-status/`),

  // 获取统计信息
  getRoommatePostStats: () => api.get('/roommates/stats/'),

  // 上传室友图片
  uploadRoommateImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/roommates/upload-image/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const rentalAPI = {
  getRentals: (params) => api.get('/properties/rentals/', { params }),
  getMyRentals: () => api.get('/properties/my-listings/'),
  getRental: (slugOrId) => api.get(`/properties/rentals/${slugOrId}/`),
  createRental: (data) => api.post('/properties/rentals/', data),
  updateRental: (slugOrId, data) => api.put(`/properties/rentals/${slugOrId}/`, data),
  deleteRental: (slugOrId) => api.delete(`/properties/rentals/${slugOrId}/`),
  uploadRentalImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/properties/upload-image/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  contactLandlord: (data) => api.post('/properties/contact-landlord/', data),
};

export const userAPI = {
  toggleFavorite: (rental_id) => api.post('/auth/favorites/toggle/', { rental_id }),
  getFavorites: () => api.get('/auth/favorites/'),
  // Progress相关API
  createProgress: (rental_id) => api.post('/properties/progress/create/', { rental: rental_id }),
  getProgress: () => api.get('/properties/progress/list/'),
  updateProgress: (progress_id, data) => api.patch(`/properties/progress/${progress_id}/update/`, data),
};

export default api; 